import { useCallback } from 'react';
import classNames from 'classnames';
import { useMediaQuery } from 'usehooks-ts';

import { ExpandButton } from '../../button/expand-button/expand-button';
import Typography from '../../typography/typography';
import { Breakpoints, getMediaQuery } from '../../../utils/breakpoints';

import VerticalTabsContentBlockComponent from '../vertical-tabs-content-block/vertical-tabs-content-block.component';

interface VerticalTabsNavigationProps {
  cqItemsOrder: string[];
  activeItemIndex: number | null;
  setActiveItemIndex: (id: number | null) => void;
  cqItems: Record<string, any>;
  hasGradientBackground?: boolean;
  hasMidBlueBackground?: boolean;
}

export const VerticalTabsNavigation = ({
  cqItemsOrder,
  activeItemIndex,
  setActiveItemIndex,
  cqItems,
  hasGradientBackground,
  hasMidBlueBackground,
}: VerticalTabsNavigationProps) => {
  const isMobile = useMediaQuery(getMediaQuery(Breakpoints.MOBILE, 'down'));
  const darkBg = hasGradientBackground || hasMidBlueBackground;

  const handleTabItemClick = (index: number | null) => {
    if (!isMobile && isExpandedOrActive(index)) {
      return;
    }

    setActiveItemIndex(isExpandedOrActive(index) && isMobile ? null : index);
  };

  const isExpandedOrActive = useCallback(
    (index: number | null) => {
      return index === activeItemIndex;
    },
    [activeItemIndex]
  );

  const getStyles = useCallback(
    (index) => ({
      navigationItem: {
        'snowflake-vertical-tabs-navigation-item': true,
        'navigation-item-light-bg': !darkBg,
        'navigation-item-dark-bg': darkBg,
        'navigation-item-on-mobile': isMobile,
        active: isExpandedOrActive(index),
      },
    }),
    [darkBg, isExpandedOrActive, isMobile]
  );

  return (
    <>
      {cqItemsOrder?.map((item, index) => (
        <div
          key={item}
          className={classNames('snowflake-vertical-tabs-navigation-container', {
            'tab-content-expanded': isMobile && isExpandedOrActive(index),
          })}
        >
          <div
            data-testid="snowflake-vertical-tabs-navigation-item"
            onClick={() => handleTabItemClick(index)}
            className={classNames(getStyles(index).navigationItem)}
          >
            <Typography variant="body1">{cqItems[item as any]['cq:panelTitle']}</Typography>

            {isMobile && (
              <ExpandButton
                testId="snowflake-expand-vertical-tab-content-expand-btn"
                expanded={isExpandedOrActive(index)}
                onClick={() => handleTabItemClick(index)}
                darkBg={darkBg}
                blackButtonOnExpandedState
              />
            )}
          </div>
          {isMobile && isExpandedOrActive(index) && (
            <VerticalTabsContentBlockComponent
              {...cqItems[item]}
              hasMidBlueBackground={hasMidBlueBackground}
              hasGradientBackground={hasGradientBackground}
            />
          )}
        </div>
      ))}
    </>
  );
};
